/**
 * HTML5 Form Validation Translation
 *
 * Automatically applies translated validation messages to HTML5 form elements
 * using the Constraint Validation API (setCustomValidity).
 *
 * Uses translations from the global window.i18n object.
 */

/**
 * Get translated validation message based on element type and validation type
 *
 * @param {HTMLElement} element - The form element
 * @param {string} validationType - Type of validation ('required', 'email', etc.)
 * @returns {string} - Translated validation message
 */
function getValidationMessage(element, validationType) {
    // Check if translations are available
    if (!window.i18n) {
        return '';
    }

    let messageKey;

    // Determine message based on element type and validation
    if (validationType === 'required') {
        // Different messages for select vs input elements
        if (element.tagName.toLowerCase() === 'select') {
            messageKey = 'Vælg venligst et element på listen';
        } else {
            messageKey = 'Dette felt er påkrævet';
        }
    } else if (validationType === 'email') {
        messageKey = 'Indtast venligst en gyldig e-mailadresse';
    }

    // Return translated message, fallback to key if translation not found
    return window.i18n[messageKey] || messageKey;
}

/**
 * Apply validation message handler to a single element
 *
 * @param {HTMLElement} element - The form element to enhance
 */
function applyValidationToElement(element) {
    // Handle 'invalid' event - set custom message
    element.addEventListener('invalid', function(event) {
        event.preventDefault(); // Prevent default browser message

        // Determine which validation failed
        if (this.validity.valueMissing) {
            // Required field is empty
            this.setCustomValidity(getValidationMessage(this, 'required'));
        } else if (this.validity.typeMismatch && this.type === 'email') {
            // Email format is invalid
            this.setCustomValidity(getValidationMessage(this, 'email'));
        } else {
            // For other validation types, clear custom message (use browser default)
            this.setCustomValidity('');
        }
    });

    // Handle 'input' event - clear custom message when user types
    element.addEventListener('input', function() {
        this.setCustomValidity('');
    });

    // Handle 'change' event - clear custom message for select elements
    if (element.tagName.toLowerCase() === 'select') {
        element.addEventListener('change', function() {
            this.setCustomValidity('');
        });
    }
}

/**
 * Intercept form submissions to trigger HTML5 validation
 * @param {HTMLFormElement} form - The form element
 */
function interceptFormSubmit(form) {
    // Add capture-phase submit listener (runs before Vue's @submit.prevent)
    form.addEventListener('submit', function(event) {
        // Find all required/validated elements in this form
        const elementsToValidate = form.querySelectorAll('input[required], select[required], textarea[required], input[type="email"]');

        let isValid = true;
        for (const element of elementsToValidate) {
            // Clear any previous custom validity to allow re-validation
            element.setCustomValidity('');

            if (!element.checkValidity()) {
                // Set translated custom message
                if (element.validity.valueMissing) {
                    element.setCustomValidity(getValidationMessage(element, 'required'));
                } else if (element.validity.typeMismatch && element.type === 'email') {
                    element.setCustomValidity(getValidationMessage(element, 'email'));
                }

                // Show validation message
                element.reportValidity();
                isValid = false;
                break; // Stop at first invalid element
            }
        }

        if (!isValid) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
        }
    }, true); // Use capture phase to run before Vue handlers
}

/**
 * Initialize validation translation on all form elements
 */
function initializeValidationTranslation() {
    // Find all elements that could have HTML5 validation
    const selectors = [
        'input[required]',
        'select[required]',
        'textarea[required]',
        'input[type="email"]',
    ];

    const elements = document.querySelectorAll(selectors.join(', '));

    elements.forEach(element => {
        applyValidationToElement(element);
    });

    // Find all forms and intercept their submissions
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
        interceptFormSubmit(form);
    });

    // Use event delegation on document to catch ALL button clicks (including Vue-rendered buttons)
    document.addEventListener('click', function(event) {
        const target = event.target;

        // Check if the clicked element is or is inside a submit button
        // Only catch actual submit buttons, not action buttons with type="button"
        const button = target.closest('button[type="submit"], button:not([type]), input[type="submit"], [type="submit"]');

        if (!button) {
            return; // Not a button, ignore
        }

        // Find the form this button belongs to
        const form = button.closest('form');
        if (!form) {
            return;
        }

        // Find all required/validated elements in this form
        const elementsToValidate = form.querySelectorAll('input[required], select[required], textarea[required], input[type="email"]');

        let isValid = true;
        for (const element of elementsToValidate) {
            // Clear any previous custom validity to allow re-validation
            element.setCustomValidity('');

            if (!element.checkValidity()) {
                // Set translated custom message
                if (element.validity.valueMissing) {
                    const message = getValidationMessage(element, 'required');
                    element.setCustomValidity(message);
                } else if (element.validity.typeMismatch && element.type === 'email') {
                    const message = getValidationMessage(element, 'email');
                    element.setCustomValidity(message);
                }

                // Show validation message
                element.reportValidity();
                isValid = false;
                break; // Stop at first invalid element
            }
        }

        if (!isValid) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
        }
    }, true); // Use capture phase to run before Vue handlers
}

/**
 * Auto-initialize on DOM content loaded
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeValidationTranslation);
} else {
    // DOM already loaded
    initializeValidationTranslation();
}

/**
 * Export for manual re-initialization if needed (e.g., after Vue mounts new forms)
 */
export { initializeValidationTranslation, applyValidationToElement };
